/**
 * Free Gift Manager
 * Handles free gift functionality for Shopify cart
 */

class FreeGiftManager {
  constructor() {
    this.settings = window.freeGiftSettings || {};
    this.giftProductId = this.settings.freeGiftProductId || '43336468889634';
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    this.bindEvents();
    this.checkGiftEligibility();
    this.isInitialized = true;
    
    console.log('FreeGiftManager initialized');
  }

  bindEvents() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail);
    });

    // Listen for cart drawer open/close
    document.addEventListener('cart-drawer:open', () => {
      this.checkGiftEligibility();
    });

    // Listen for quantity changes
    document.addEventListener('quantity:change', () => {
      setTimeout(() => this.checkGiftEligibility(), 500);
    });

    // Multiple initialization checks for reliability
    this.setupInitialChecks();
  }

  setupInitialChecks() {
    // Check immediately if DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.performInitialCheck();
      });
    } else {
      this.performInitialCheck();
    }

    // Also check on window load as fallback
    window.addEventListener('load', () => {
      setTimeout(() => this.checkGiftEligibility(), 100);
    });

    // Check when cart drawer elements are available
    this.waitForCartElements();
  }

  async performInitialCheck() {
    // Initial check with retries
    let attempts = 0;
    const maxAttempts = 3;
    
    const checkWithRetry = async () => {
      try {
        await this.checkGiftEligibility();
        console.log('Free gift initial check completed successfully');
      } catch (error) {
        attempts++;
        console.warn(`Free gift check attempt ${attempts} failed:`, error);
        
        if (attempts < maxAttempts) {
          setTimeout(checkWithRetry, 500 * attempts);
        }
      }
    };

    checkWithRetry();
  }

  waitForCartElements() {
    // Wait for cart elements to be available
    const checkElements = () => {
      const cartExists = document.querySelector('.mini-cart__navigation') || 
                        document.querySelector('.cart-items tbody');
      
      if (cartExists) {
        setTimeout(() => this.checkGiftEligibility(), 200);
      } else {
        setTimeout(checkElements, 100);
      }
    };

    setTimeout(checkElements, 100);
  }

  async handleCartUpdate(cartData) {
    if (cartData && cartData.items) {
      this.updateGiftDisplay(cartData);
    } else {
      // Fetch fresh cart data if not provided
      const cart = await this.fetchCart();
      this.updateGiftDisplay(cart);
    }
  }

  async checkGiftEligibility(cart = null) {
    try {
      if (!cart) {
        cart = await this.fetchCart();
      }
      
      const eligible = this.isEligibleForGift(cart);
      this.updateGiftDisplay(cart, eligible);
    } catch (error) {
      console.warn('FreeGiftManager: Error checking eligibility', error);
    }
  }

  isEligibleForGift(cart) {
    const eligibleByAmount = this.checkAmountEligibility(cart);
    const eligibleByCollection = this.checkCollectionEligibility(cart);
    
    return eligibleByAmount || eligibleByCollection;
  }

  checkAmountEligibility(cart) {
    if (!this.settings.isFreeGiftByAmount) return false;
    return cart.total_price >= this.settings.cartAmountFreeGift;
  }

  checkCollectionEligibility(cart) {
    if (!this.settings.isFreeGiftByCollection) return false;
    
    const collections = this.getItemCollections(cart);
    const hasCorpinos = collections.includes('Corpiños');
    const hasBombachas = collections.includes('Bombachas');
    const hasEnterizas = collections.includes('Enterizas');
    
    return (hasBombachas && hasCorpinos) || hasEnterizas;
  }

  getItemCollections(cart) {
    return cart.items
      .filter(item => item.product_type === 'Bombachas' || item.product_type === 'Corpiños' || item.product_type === 'Enterizas')
      .map(item => item.product_type);
  }

  updateGiftDisplay(cart, eligible = null) {
    if (eligible === null) {
      eligible = this.isEligibleForGift(cart);
    }

    this.updateMinicart(eligible, cart);
    this.updateCartPage(eligible, cart);
    this.updateBanners(eligible);
  }

  updateMinicart(eligible, cart) {
    const minicartContainer = document.querySelector('.mini-cart__navigation');
    if (!minicartContainer) return;

    const existingGift = minicartContainer.querySelector('.free-gift-item');
    
    if (eligible) {
      if (!existingGift) {
        const giftElement = this.createMinicartGiftElement();
        // Insert as first item after regular cart items
        minicartContainer.appendChild(giftElement);
      } else {
        existingGift.style.display = 'block';
      }
    } else if (existingGift) {
      existingGift.style.display = 'none';
    }

    this.updateCartCount(cart, eligible);
  }

  updateCartPage(eligible, cart) {
    const cartTable = document.querySelector('.cart-items tbody');
    if (!cartTable) return;

    const existingGift = cartTable.querySelector('.cart-gift-item');
    
    if (eligible) {
      if (!existingGift) {
        const giftRow = this.createCartGiftElement();
        cartTable.appendChild(giftRow);
      } else {
        existingGift.style.display = 'table-row';
      }
    } else if (existingGift) {
      existingGift.style.display = 'none';
    }
  }

  updateBanners(eligible) {
    const banners = document.querySelectorAll('.gift-banner, .gift-banner-cart');
    banners.forEach(banner => {
      banner.style.display = eligible ? 'none' : 'flex';
    });
  }

  createMinicartGiftElement() {
    const li = document.createElement('li');
    li.classList.add('free-gift-item');
    li.setAttribute('data-variant-id', this.giftProductId);
    li.setAttribute('data-handle', 'free-gift');
    li.setAttribute('data-quantity', '1');
    li.setAttribute('data-price', '0');

    li.innerHTML = `
      <div class="product-container">
        ${this.settings.freeGiftImage ? `
          <div>
            <div class="product-image media-wrapper media-wrapper--small">
              <div class="media media--adapt" style="--image-ratio-percent: 100%;">
                <img 
                  src="${this.settings.freeGiftImage}" 
                  alt="${this.settings.freeGiftAltText || 'Free Gift'}"
                  width="70" 
                  height="70" 
                  loading="lazy"
                  is="lazy-image"
                />
              </div>
            </div>
          </div>
        ` : ''}
        <div class="product-description">
          <div class="product-content">
            <div class="link product-title">${this.settings.freeGiftProductName}</div>
            <dd class="price">
              <price-money><bdi>GRATIS</bdi></price-money>
            </dd>
          </div>
          <dl>
            <div class="product-option">
              <dd>${this.settings.freeGiftVariantName}</dd>
            </div>
          </dl>
          <div class="gift-component">
            <img src="${this.settings.freeGiftIcon || window.location.origin + '/assets/gift-icon.png'}" width="16" height="16" alt="Gift icon">
            <p class="gift-text">${this.settings.freeGiftText}</p>
          </div>
        </div>
      </div>
    `;

    return li;
  }

  createCartGiftElement() {
    const tr = document.createElement('tr');
    tr.classList.add('cart-item', 'cart-gift-item');
    tr.setAttribute('id', `CartItem-gift-${this.giftProductId}`);

    tr.innerHTML = `
      <td class="cart-item__media">
        ${this.settings.freeGiftImage ? `
          <span class="media-wrapper media-wrapper--small">
            <div class="media media--adapt" style="--image-ratio-percent: 100%;">
              <img class="cart-item__image"
                src="${this.settings.freeGiftImage}"
                alt="${this.settings.freeGiftAltText || 'Free Gift'}"
                loading="lazy"
                width="70"
                height="70"
                is="lazy-image"
              />
            </div>
          </span>
        ` : ''}
      </td>
      <td class="cart-item__details">
        <div>
          <div class="cart-item__name">${this.settings.freeGiftProductName}</div>
        </div>
        <dl>
          <div class="product-option">
            <dd>${this.settings.freeGiftVariantName}</dd>
          </div>
        </dl>
        <div class="gift-badge-cart">
          <img src="${this.settings.freeGiftIcon || window.location.origin + '/assets/gift-icon.png'}" width="16" height="16" alt="Gift icon">
          <p class="gift-text">${this.settings.freeGiftText}</p>
        </div>
      </td>
      <td class="cart-item__prices small-hide right">
        <span class="price price--end">
          <price-money><bdi>GRATIS</bdi></price-money>
        </span>
      </td>
      <td class="medium-hide large-up-hide right">
        <span class="price price--end">
          <price-money><bdi>GRATIS</bdi></price-money>
        </span>
      </td>
      <td class="cart-item__quantity">
        <!-- No quantity selector for gift items -->
      </td>
      <td class="cart-item__totals right">
        <span class="price price--end">
          <price-money><bdi>GRATIS</bdi></price-money>
        </span>
      </td>
    `;

    return tr;
  }

  updateCartCount(cart, eligible) {
    const countElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble');
    if (countElements.length === 0) return;

    let totalCount = cart.item_count;
    
    // Add 1 for the visual gift if eligible
    if (eligible) {
      totalCount += 1;
    }
    
    countElements.forEach(el => {
      if (el.textContent !== undefined) {
        el.textContent = totalCount;
      }
    });
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      
      return await response.json();
    } catch (error) {
      console.error('FreeGiftManager: Error fetching cart', error);
      throw error;
    }
  }

  // Public method to manually trigger gift check
  refresh() {
    this.checkGiftEligibility();
  }

  // Public method to check if feature is enabled
  isEnabled() {
    return this.settings.isFreeGiftByAmount || this.settings.isFreeGiftByCollection;
  }

  // Force refresh of gift display - useful for debugging or edge cases
  async forceRefresh() {
    try {
      const cart = await this.fetchCart();
      console.log('Force refresh - Cart data:', cart);
      this.updateGiftDisplay(cart);
    } catch (error) {
      console.error('Force refresh failed:', error);
    }
  }

  // Start periodic check (useful for debugging)
  startPeriodicCheck(intervalMs = 10000) {
    if (this.periodicInterval) {
      clearInterval(this.periodicInterval);
    }
    
    this.periodicInterval = setInterval(() => {
      console.log('Periodic gift check...');
      this.checkGiftEligibility();
    }, intervalMs);
  }

  // Stop periodic check
  stopPeriodicCheck() {
    if (this.periodicInterval) {
      clearInterval(this.periodicInterval);
      this.periodicInterval = null;
    }
  }
}

// Robust auto-initialization with multiple triggers
function initializeFreeGiftManager() {
  if (window.freeGiftManager) {
    // Already initialized, just refresh
    window.freeGiftManager.refresh();
    return;
  }

  if (window.freeGiftSettings && (window.freeGiftSettings.isFreeGiftByAmount || window.freeGiftSettings.isFreeGiftByCollection)) {
    console.log('Initializing FreeGiftManager...');
    window.freeGiftManager = new FreeGiftManager();
  }
}

// Multiple initialization points for maximum reliability
document.addEventListener('DOMContentLoaded', initializeFreeGiftManager);

// Fallback for when DOMContentLoaded might have already fired
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFreeGiftManager);
} else {
  // DOM is already ready
  setTimeout(initializeFreeGiftManager, 50);
}

// Additional fallback on window load
window.addEventListener('load', () => {
  setTimeout(initializeFreeGiftManager, 100);
  
  // Final check after everything is loaded
  setTimeout(() => {
    if (window.freeGiftManager) {
      console.log('Final post-load gift check...');
      window.freeGiftManager.forceRefresh();
    }
  }, 1000);
});

// Listen for page visibility changes (when coming back from another tab)
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && window.freeGiftManager) {
    setTimeout(() => window.freeGiftManager.refresh(), 200);
  }
});

// Listen for focus events (when window regains focus)
window.addEventListener('focus', () => {
  if (window.freeGiftManager) {
    setTimeout(() => window.freeGiftManager.refresh(), 200);
  }
});

// Export for manual initialization if needed
window.FreeGiftManager = FreeGiftManager;
